import { useEffect, useCallback } from 'react';
import { type Subscription } from 'rxjs';
import { TranscriptionCapability } from '@doxyme/extensions-bridge';
import { useAddCapability } from '@doxyme/extensions-bridge-react';
import { bridge } from '../../../features/extensions/instances';
import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import { useService } from '../../useService';
import { useAppSelector } from '../../useReduxToolkit';

/**
 * Hook responsible for providing callbacks required for transcription to the Extensions SDK
 */
export function useExtensionsAddTranscription(): void {
  const transcription = useAddCapability(
    bridge,
    () => new TranscriptionCapability(),
  );

  const { sessionId } = useAppSelector(getApolloSessionDetails);

  const service = useService('transcription');

  const subscribeToTranscriptionStatus = useCallback((): Subscription => {
    return service.transcriptionStatus.subscribe(
      async ({ isSuccess, transcriptionId, errorMessage, data }) => {
        if (!isSuccess) {
          transcription.completed({
            isSuccess,
            transcriptionId,
            errorMessage: errorMessage as string,
          });
          return;
        }

        if (data?.url !== undefined) {
          try {
            const payload = await service.downloadTranscription(data.url);
            transcription.completed({
              isSuccess,
              transcriptionId,
              data: {
                ...payload,
                audioUrl: data.audioUrl,
              },
            });
          } catch (error) {
            transcription.completed({
              isSuccess: false,
              errorMessage: `${error}`,
              transcriptionId,
            });
          }
        } else if (data?.audioUrl !== undefined) {
          transcription.completed({
            isSuccess,
            transcriptionId,
            data: { text: '', audioUrl: data.audioUrl },
          });
        }
        void service.delete(transcriptionId);
      },
    );
  }, []);

  useEffect(() => {
    const unsubscribes = [
      transcription.onStart(async (output): Promise<string> => {
        if (sessionId === undefined) {
          throw new Error('SessionId is not defined');
        }

        return service.start(sessionId, output);
      }),
      transcription.onStop(
        async (transcriptionId): Promise<void> => service.stop(transcriptionId),
      ),
      transcription.onCancel(
        async (transcriptionId): Promise<void> =>
          service.cancel(transcriptionId),
      ),
    ];
    const subscriber = subscribeToTranscriptionStatus();

    return () => {
      for (const unsubscribe of unsubscribes) {
        unsubscribe();
      }
      subscriber.unsubscribe();
    };
  }, [sessionId]);
}
